import React, { Suspense, lazy, useEffect, useState } from "react";
import { observer } from "mobx-react";
import { useHistory } from "react-router-dom";
import { signalsData } from "Stores/signalsData";
import { ReactComponent as LinkedinIcon } from "Assets/svg/linkedin.svg";
import ContactAvatar from "Pages/Dashboard/contacts/ContactAvatar";
import { AddContactsToList } from "Pages/Dashboard/contacts/AddContactsToList";
import SignalIcon from "./SignalIcon";
import {
  addToList, addPersonToList, addToSalesgear, addToSequence, draftEmail, ensureContactLists,
  findEmailFromLinkedIn, findPhoneFromLinkedIn, reviewDraft
} from "./feedActions";
import { greetByName } from "./signalMeta";

/**
 * Who to reach, as people you can act on: the person a feed entry names (a new leader), as a card that opens the
 * app's contact panel.
 * Someone not saved yet is added on the first action that needs a contact (Add to Salesgear, sequence, call).
 */

// The lookup returns at most 3 (one cached search per account and persona); credits are spent only on a reveal.
export const SHOWN = 3;

// The app's contact card is heavy (editor, dialer); loaded the first time someone opens a person.
const InsertContactCard = lazy(() => import("Components/ContactCard/InsertContactCard"));

const ADDING = ["Adding them…", "Saving their profile…", "Checking your contacts…", "Linking their company…", "Almost there…"];

/** One message after another while `on`, so a few seconds' wait reads as progress. */
const useRotating = (on, messages, everyMs = 1500) => {
  const [i, setI] = useState(0);
  useEffect(() => {
    setI(0);
    if (!on) return undefined;
    const t = setInterval(() => setI(n => Math.min(n + 1, messages.length - 1)), everyMs);
    return () => clearInterval(t);
  }, [on]); // eslint-disable-line react-hooks/exhaustive-deps
  return messages[i];
};

/** One person's actions: every one keeps what it learned (contact id, email, phone) so the next one reuses it. */
const usePerson = (initial, { company, domain, query, draft, onDrafted }) => {
  const history = useHistory();
  const [p, setP] = useState(initial);
  const [busy, setBusy] = useState("");
  const [listTrigger, setListTrigger] = useState(0);
  useEffect(() => setP(prev => ({ ...initial, ...pick(prev) })), [initial]); // eslint-disable-line react-hooks/exhaustive-deps

  const asked = { ...p, company, domain };
  const learn = got => {
    if (!got) return;
    setP(prev => ({ ...prev, ...got }));
    if (query && got.alreadyContactId) signalsData.markPersonContact(query, p.linkedinUrl, got.alreadyContactId, got.email);
  };
  const run = (kind, fn) => async () => {
    setBusy(kind);
    try { return await fn(); } finally { setBusy(""); }
  };
  // A contact id, adding them first when they aren't saved yet.
  const ensureContact = async () => {
    if (p.alreadyContactId) return p.alreadyContactId;
    const id = await addToSalesgear(asked, { company, domain });
    if (id) learn({ alreadyContactId: id });
    return id;
  };

  const actions = {
    add: run("add", ensureContact),
    findEmail: run("email", async () => {
      const hit = await findEmailFromLinkedIn(asked);
      if (hit) learn({ email: hit.email, alreadyContactId: hit.contactId || p.alreadyContactId });
    }),
    findPhone: run("phone", async () => {
      const hit = await findPhoneFromLinkedIn(asked);
      if (hit) learn({ phone: hit.phone, alreadyContactId: hit.contactId || p.alreadyContactId });
    }),
    sequence: run("sequence", async () => {
      const id = await ensureContact();
      if (id) await addToSequence(id);
    }),
    // The suggested draft when there is one (greeting them by name), else a blank email; finds their email first.
    email: run("email", async () => {
      if (draft) {
        const sent = await reviewDraft({ ...draft, body: greetByName(draft.body, p.name) }, { ...asked, contactId: p.alreadyContactId || null }, history);
        if (sent) {
          learn({ email: sent.email, alreadyContactId: sent.contactId || p.alreadyContactId });
          if (onDrafted) onDrafted(sent);
        }
        return;
      }
      let { email } = p;
      if (!email) {
        const hit = await findEmailFromLinkedIn(asked);
        if (!hit) return;
        email = hit.email;
        learn({ email, alreadyContactId: hit.contactId || p.alreadyContactId });
      }
      await draftEmail(email, history);
    }),
    // Calls go through the contact page, where the dialer checks the number and the do-not-contact stage.
    call: run("call", async () => {
      const id = await ensureContact();
      if (id) history.push(`/contacts/${id}/activity`);
    }),
    open: () => history.push(`/contacts/${p.alreadyContactId}/activity`),
    list: async () => {
      await ensureContactLists();
      setListTrigger(n => n + 1);
    }
  };
  const addPicked = async (type, listId, close = () => {}) => {
    if (listId) {
      if (p.alreadyContactId) await addToList(p.alreadyContactId, listId);
      else await addPersonToList(asked, listId, { company, domain });
    }
    close();
  };
  const listPicker = <AddContactsToList hidden contactIds={[p.alreadyContactId || p.linkedinUrl]} trigger={listTrigger} addToListCbk={addPicked} />;
  return { p, busy, actions, listPicker };
};

const pick = ({ alreadyContactId, email, phone }) => ({
  ...(alreadyContactId ? { alreadyContactId } : {}), ...(email ? { email } : {}), ...(phone ? { phone } : {})
});

const NameLine = ({ p }) => (
  <div className="sg-reach__name">
    <span>{p.name}</span>
    {p.linkedinUrl && (
      <a className="sg-pcard__li" href={p.linkedinUrl} target="_blank" rel="noopener noreferrer" title="LinkedIn profile" aria-label={`${p.name} on LinkedIn`} onClick={e => e.stopPropagation()}>
        <SignalIcon id="linkedin" size={13} />
      </a>
    )}
  </div>
);

/**
 * Who they are, in the app's own contact panel. Someone already in Salesgear opens the real contact card. Someone who
 * isn't gets the same card (its markup and classes) with what we know, Find email / Find phone number, and Add to
 * Salesgear; a find or an add saves them, and the real card takes over.
 */
const PersonPanel = observer(({ person, company, onClose }) => {
  const { p, busy, actions } = person;
  const adding = useRotating(busy === "add", ADDING);
  if (p.alreadyContactId) {
    return (
      <Suspense fallback={null}>
        <InsertContactCard
          animate
          show
          data={{ contact: { id: p.alreadyContactId }, pageType: "signals", cardType: "contacts", isContactSlider: true, closeCbk: onClose }}
        />
      </Suspense>
    );
  }
  const find = (kind, label, run) => (
    <div className="maskData" onClick={busy ? undefined : run}>
      <span className="maskText">{busy === kind ? "Finding…" : label}</span>
    </div>
  );
  return (
    <div className="contactCardSection slideInFromBottom" id="contactCard">
      <section className="contactCard">
        <div className="closeContactSlider sg-prospect__strip">
          <i className="material-icons" role="button" tabIndex={0} aria-label="Close" onClick={onClose}>close</i>
        </div>
        <div className="contactInfoCard">
          <div className="contactDetails">
            <div className="flexCenter justifyCenter">
              <h4 className="contactName">{p.name}</h4>
              <div className="contactSocialIconLink">
                {p.linkedinUrl ? (
                  <a className="linkedinIcon" href={p.linkedinUrl} target="_blank" rel="noopener noreferrer" aria-label={`${p.name} on LinkedIn`}>
                    <LinkedinIcon />
                  </a>
                ) : (
                  <span className="linkedinIcon disabled"><LinkedinIcon /></span>
                )}
              </div>
            </div>
            <div className="contactJobCompany">{p.title || "--"} @ {company || "--"}</div>
            <div className="sg-prospect__cta">
              <span>Not in Salesgear yet</span>
              <button type="button" className="sg-btn" disabled={!!busy || !p.linkedinUrl} onClick={actions.add}>
                <SignalIcon id="plus" size={14} /> {busy === "add" ? adding : "Add to Salesgear"}
              </button>
            </div>
          </div>
          <div className="contactActivityInfo">
            <div className="wrapContactActivity signals">
              <div className="contactTabFlat">
                <div className="gridCol highlightField">
                  <span className="icon">Email Address</span>
                  <span className="valueColumn">{p.email || find("email", "Find email", actions.findEmail)}</span>
                </div>
                <div className="gridCol alignStart highlightField">
                  <span className="icon">Mobile Number</span>
                  <span className="valueColumn">{p.phone || find("phone", "Find phone number", actions.findPhone)}</span>
                </div>
                <div className="gridCol">
                  <span className="icon">Location</span>
                  <span className="valueColumn">{p.location || "--"}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
});

/** One person as a card in the row: clicking anywhere on it opens their panel; the buttons act straight away. */
const PersonTile = ({ person: initial, company, domain, query, draft, onDrafted }) => {
  const person = usePerson(initial, { company, domain, query, draft, onDrafted });
  const [viewing, setViewing] = useState(false);
  const { p, busy, actions, listPicker } = person;
  const noPage = !p.linkedinUrl && !p.alreadyContactId;
  const stop = fn => e => {
    e.stopPropagation();
    fn();
  };
  return (
    <>
      <div
        className="sg-reach__tile"
        role="button"
        tabIndex={0}
        aria-label={`View ${p.name}`}
        onClick={() => setViewing(true)}
        onKeyDown={e => (e.key === "Enter" || e.key === " ") && e.target === e.currentTarget && (e.preventDefault(), setViewing(true))}
      >
        <ContactAvatar photo={p.photoUrl} name={p.name} />
        <div className="sg-reach__who">
          <NameLine p={p} />
          {p.title && <div className="sg-reach__title">{p.title}</div>}
        </div>
        <div className="sg-reach__acts">
          <button className="sg-btn sg-btn--small sg-btn--ghost" disabled={!!busy || noPage} onClick={stop(actions.email)}>
            {busy === "email" ? "Finding email…" : draft ? (p.email || p.alreadyContactId ? "Send draft" : "Find email & send · 1 credit") : p.email ? "Email" : "Find email · 1 credit"}
          </button>
          <button className="sg-ibtn" title="Add to a sequence" aria-label={`Add ${p.name} to a sequence`} disabled={!!busy || noPage} onClick={stop(actions.sequence)}><SignalIcon id="send" size={15} /></button>
          <button className="sg-ibtn" title="Add to a list" aria-label={`Add ${p.name} to a list`} disabled={!!busy || noPage} onClick={stop(actions.list)}><SignalIcon id="listAdd" size={15} /></button>
          <button className="sg-ibtn" title="Call" aria-label={`Call ${p.name}`} disabled={!!busy || noPage} onClick={stop(actions.call)}><SignalIcon id="phone" size={14} /></button>
        </div>
      </div>
      {viewing && <PersonPanel person={person} company={company} onClose={() => setViewing(false)} />}
      {listPicker}
    </>
  );
};

/** Named people (a new leader): shown as they are, no lookup. */
export const ReachPeople = ({ label, people, company, domain, query, draft, onDrafted }) => (
  <div className="sg-reach">
    {label && <div className="sg-reach__label">{label}</div>}
    {people.slice(0, SHOWN).map(p => (
      <PersonTile key={p.linkedinUrl || p.name} person={p} company={company} domain={domain} query={query} draft={draft} onDrafted={onDrafted} />
    ))}
  </div>
);
